import { useState, useCallback } from 'react';
import { VehiclePowertrain, VehicleTag } from '@/types';

export type VehicleModelSortKey = 'manufacturer' | 'modelName' | 'createdAt';
export type VehicleModelSortOrder = 'asc' | 'desc';

export interface VehicleModelFilters {
  searchQuery: string;
  selectedManufacturer: string;
  selectedVehicleType: string;
  selectedPowertrain: VehiclePowertrain | '';
  selectedTags: VehicleTag[];
  sortKey: VehicleModelSortKey;
  sortOrder: VehicleModelSortOrder;
}

export interface UseVehicleModelFiltersReturn {
  filters: VehicleModelFilters;
  updateFilter: <K extends keyof VehicleModelFilters>(
    key: K,
    value: VehicleModelFilters[K]
  ) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  toggleSort: (key: VehicleModelSortKey) => void;
}

const initialFilters: VehicleModelFilters = {
  searchQuery: '',
  selectedManufacturer: '',
  selectedVehicleType: '',
  selectedPowertrain: '',
  selectedTags: [],
  sortKey: 'manufacturer',
  sortOrder: 'asc',
};

export const useVehicleModelFilters = (): UseVehicleModelFiltersReturn => {
  const [filters, setFilters] = useState<VehicleModelFilters>(initialFilters);

  const updateFilter = useCallback(
    <K extends keyof VehicleModelFilters>(key: K, value: VehicleModelFilters[K]) => {
      setFilters(prev => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const toggleSort = useCallback((key: VehicleModelSortKey) => {
    setFilters(prev => ({
      ...prev,
      sortKey: key,
      sortOrder: prev.sortKey === key && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const hasActiveFilters =
    filters.searchQuery !== '' ||
    filters.selectedManufacturer !== '' ||
    filters.selectedVehicleType !== '' ||
    filters.selectedPowertrain !== '' ||
    filters.selectedTags.length > 0;

  return {
    filters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    toggleSort,
  };
};
